import dayjs, { Dayjs } from "dayjs";
import { BaseAPI } from "@utils/base-api";
import {
  PouchSize,
} from "libs/cat-calculations/src/lib/cat-calculations.types";
import { HttpException, Logger } from "@nestjs/common";
import { AxiosRequestConfig, AxiosRequestHeaders, AxiosResponse } from "axios";
import { TCreditReason } from "libs/constants/discounts-and-credit/src";
import { IActionResponseFailure } from "@forms/schema";
import {
  PrimaryCancellationReason,
  PrimaryScoopCancellationReason,
} from "libs/types/ai-gateway/src/types/cancellation-reasons";

import { EStatusCodes } from "@/constants/state";
import { DiscountApplication } from "@/primary-models";

import { TRequest, TResponse as TRequestResponse } from "../../../api.types";

import { IPossibleDeliveryDates } from "./types/delivery-dates";
import { AddOnName, ExtraProduct, FreshRecipeName } from "./types/products";
import {
  ICustomerCatsResponse,
  MealPlanType,
  ICat,
  TCatMedicalConditions,
  IStoreCatsData,
} from "./types/cats";
import { ICustomer, ICustomerFull } from "./types";
import { TLegacyOrderCollection } from "./types/orders";
import {
  ILitterCheckoutRequest,
  AddOnPlanType,
  IFreshOptionsRequest,
  IFreshOptionsNewCustomerRequest,
  IFreshOptionsResponse,
  UserId,
  ICreateFreshCustomer,
  ICreateCustomerResponse,
} from "./types/checkout";
import {
  ActivateSubscription,
  ICustomerOrder,
  IDelaySuccessResult,
  IDiscount,
  IMySubscriptions,
  OrderType,
  SubscriptionStatus,
  SubscriptionType,
} from "./types/subscriptions";

interface SuccessfulValidationSet<T> {
  success: true;
  value: T;
}

export class CustomerAPI extends BaseAPI {

  private readonly logger = new Logger(CustomerAPI.name);

  public async getDiscountCode(): Promise<string> {
    try {
      const data = await this.getAllSubscriptions();
      return data?.discountData?.discount?.code ?? "";
    } catch (error) {
      console.error(error);
    }
    return "";
  }

  public async getDiscountData(): Promise<IDiscount | undefined> {
    const data = await this.getAllSubscriptions();
    return data?.discountData?.discount;
  }

  public async getFullCustomer({
    allSubscriptions = false,
    show2404Product = false,
  }: {
    allSubscriptions?: boolean,
    show2404Product?: boolean,
  } = {}): Promise<ICustomerFull> {
    const result = await this.axios.get("/customer/full", {
      params: {
        allSubscriptions,
        show2404Product,
      },
    });
    return result.data;
  }

  public async getCustomer(): Promise<ICustomer> {
    const { customer } = await this.getFullCustomer();
    return customer;
  }

  public async getAllSubscriptions({
    show2404Product = false,
    headers,
  }: {
    show2404Product?: boolean
    headers?: AxiosRequestHeaders,
  } = {}): Promise<IMySubscriptions> {

    // @ts-ignore
    const options: AxiosRequestConfig<any> = { // eslint-disable-line
      params: {
        show2404Product,
      },
      headers,
    };

    const result = await this.axios.get(
      "/customer/getAllSubscriptions",
      options,
    );
    return result.data;
  }

  public async reactivateSubscription(
    subscriptionId: string | number,
    deliveryDate: Dayjs,
  ): Promise<ActivateSubscription> {
    const result = await this.axios.post("/customer/subscriptions", {
      rechargeSubscriptionIds: [ Number(subscriptionId) ],
      nextDeliveryDate: deliveryDate.format("YYYY-MM-DD"),
    });
    return result.data;
  }

  public async addDiscountCode(
    discountCode?: string,
  ): Promise<DiscountApplication> {
    if (discountCode === undefined) {
      throw new HttpException({ message: "No discount code" }, EStatusCodes.NOT_ACCEPTABLE);
    }

    try {
      const response = await this.axios.post(
        `/customer/discount/add?discountCode=${encodeURIComponent(discountCode)}`,

      );
      return response.data;
    } catch {
      throw new HttpException({ message: "Bad request" }, EStatusCodes.BAD_REQUEST);
    }

  }

  public async removeDiscountCode(): Promise<void> {
    await this.axios.post(
      "/customer/discount/remove",
    );
  }

  public async getPossibleSubscriptions(): Promise<void> {
    const result = await this.axios.get(
      "/customer/extraPlan/getPossibleSubscriptions",
    );
    return result.data;
  }

  /** @param numberOfDays
   *  @param subscriptionIds
   *  @param subscriptionStatus if set, then only subscriptions in that state will be considered when determining the
   *  subscriptionType and addonPlanType to use when searching for matching rules.
   *  @param bankHoliday should be false in most cases (unless maybe you're an admin), since we want customers to only
   *  be able to choose from non-disabled delivery dates regardless of whether or not they've been marked as bank
   *  holidays
   */
  public async getPossibleDeliveryDatesSub(
    numberOfDays: number | undefined,
    subscriptionIds: number[] | undefined,
    subscriptionStatus: SubscriptionStatus | undefined,
    bankHoliday: boolean | undefined,
  ): Promise<IPossibleDeliveryDates> {
    const params = new URLSearchParams();
    if (numberOfDays) { params.set("numberOfDays", numberOfDays.toString()); }
    if (subscriptionIds) { subscriptionIds.forEach((subscriptionId) => params.append("subscriptionId", subscriptionId.toString())); }
    if (subscriptionStatus) { params.set("subscriptionStatus", subscriptionStatus); }
    if (bankHoliday) { params.set("bankHoliday", "true"); }
    const result = await this.axios.get("/customer/getPossibleDeliveryDatesSub", {
      params,
    });
    return result.data;
  }

  /** If a parameter is not set, then it is treated as if all the types apply.
   *  So if 2022-12-06 is blocked for fresh and 2022-12-07 is blocked for addon and
   *  subscriptionType is not set, then neither 2022-12-06 nor 2022-12-07 will be
   *  available in the returned data.
   *  @param orderType
   *  @param numberOfDays
   *  @param subscriptionType
   *  @param addonPlanType
   *  @param bankHoliday if set to true, then only bank holidays will be excluded. Else
   *  non-bank holiday delivery dates that have been disabled will be excluded as well.
   *  Should probably be false for most use cases. */
  public async getPossibleDeliveryDatesType(
    orderType: OrderType,
    numberOfDays?: number,
    subscriptionType?: SubscriptionType,
    addonPlanType?: ExtraProduct,
    bankHoliday?: boolean,
  ): Promise<IPossibleDeliveryDates> {
    const params = new URLSearchParams();
    params.set("orderType", orderType);
    if (numberOfDays) { params.set("numberOfDays", numberOfDays.toString()); }
    if (subscriptionType) { params.set("subscriptionType", subscriptionType); }
    if (addonPlanType) { params.set("addonPlanType", addonPlanType); }
    if (bankHoliday) { params.set("bankHoliday", "true"); }
    const result = await this.axios.get("/getPossibleDeliveryDatesType", { params });
    return result.data;
  }

  public async delay(
    newDeliveryDate: Dayjs,
    mainSubscriptionIds: number[] | undefined,
    reason: unknown,
  ): Promise<IDelaySuccessResult> {
    const result = await this.axios.post("/customer/charges/next/date", {
      newDeliveryDate: dayjs(newDeliveryDate).format("YYYY-MM-DD"),
      mainSubscriptionIds,
      reason,
    }, {
    });
    return result.data;
  }

  public async getOrders(
    subscriptionId: string | undefined,
    catId: string | undefined,
    catName: string | undefined,
  ): Promise<ICustomerOrder[]> {
    const params = new URLSearchParams();
    if (subscriptionId) { params.set("subscriptionId", subscriptionId.toString()); }
    if (catId) { params.set("catId", catId); }
    if (catName) { params.set("catName", catName); }
    const result = await this.axios.get("/customer/orders/all", {
      params,
    });
    return result.data;
  }

  public async getAllCats() {
    const response = await this.axios.get<ICustomerCatsResponse>(
      "/customer/cats?usable=true&all=true",
    );
    return response.data;
  }

  public async getFreshCats() {
    const response = await this.axios.get<ICustomerCatsResponse>(
      "/customer/cats?usable=true",
    );
    return response.data;
  }

  public async getAllDestructuredCats(): TRequestResponse<ICustomerCatsResponse> {
    try {
      const response = await this.axios.get(
        "/customer/cats",
      );
      return response.data.cats;
    } catch (error) {
      return { error: error as Error };
    }
  }

  public async visitedPage(
    pageUrl: string,
    token: string,
  ): Promise<unknown> {
    const result = await this.axios.post(
      "/customer/visitedPage",
      { pageUrl },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        withCredentials: true,
      },
    );
    return result.data;
  }

  public async changeMealPlan(req: ChangeMealPlanRequest): Promise<unknown> {
    const result = await this.axios.put(
      "/customer/mealPlan",
      req,
      {},
    );
    return result.data;
  }

  public async addNibblesTrial(request: AddNibblesTrialRequest): Promise<string> {
    const result = await this.axios.post(
      "/customer/addTrialNibblesWithRepeat",
      request,
      {},
    );
    return result.data;
  }

  public async getAvailableTraySizes(planType: MealPlanType): Promise<ITraySizeResource[]> {

    const result: TRequest<TMealPlanResponse> = await this.axios.get(
      "/availableMealPlans",
      {},
    );

    if (result.data?.[planType]?.traySize) {
      return Object.entries(result.data[planType].traySize)?.map(([ key, value ]) => ({
        traySize: key as PouchSize,
        ...value,
      }));
    }

    return [];

  }

  public async getAvailableMealPlanTypes(): Promise<IMealPlanTypeResource[]> {
    const result: TRequest<TMealPlanResponse> = await this.axios.get(
      "/availableMealPlans",
      {},
    );

    return Object.values(result.data);
  }

  public async getAvailableFreshPlans(
    user: UserId,
    request: IFreshOptionsRequest | IFreshOptionsNewCustomerRequest,
  ) : Promise<IFreshOptionsResponse | null > {
    const config = {
      headers: user.accessToken ? {
        Authorization: `Bearer ${user.accessToken}`,
      } : undefined,
      withCredentials: true,
    };
    const response = await this.axios.post(
      "/checkout/recipesAndQuantity",
      request,
      config,
    );
    return response.data;
  }

  public async updateFreshSizeAndCadence(req: UpdateFreshSizeAndCadenceRequest): Promise<unknown> {
    const result = await this.axios.post(
      "/customer/cats/mealPlan",
      req,
    );
    return result.data;

  }

  public async getAllOrders(): Promise<TLegacyOrderCollection> {
    const result = await this.axios.get(
      "/customer/orders/all",
    );
    return result.data;
  }

  public async changeAddons(req: IChangeAddonsRequest): Promise<unknown> {
    const result = await this.axios.post(
      "/customer/changeAddons",
      req,
    );
    return result.data;
  }

  public async changeRecipes(req: IChangeRecipesRequest): Promise<unknown> {
    const result = await this.axios.post(
      "/customer/changeRecipes",
      req,
    );
    return result.data;
  }

  public async updateCustomerDetails(req: IUpdateCustomerDetailsRequest): Promise<unknown> {
    const result = await this.axios.put(
      "/customer/update",
      req,
    );
    return result.data;
  }

  public async updateCatDetails(req: IUpdateCatDetailsRequest): Promise<AxiosResponse<ICat>> {
    const result = this.axios.post(
      "/customer/cats/update",
      req,
    );

    return result;
  }

  public async checkoutLitter(
    req: ILitterCheckoutRequest,
    product: AddOnPlanType,
  ): Promise<AxiosResponse<unknown>> {
    const result = await this.axios.post(`/customer/extraPlan/checkout?addonPlanType=${product}`, req);
    return result;
  }

  public async addNewCat(data: IStoreCatsData): TRequestResponse<unknown> {
    try {
      const response = await this.axios.post(
        "/customer/cats/addMulti",
        data,
      );
      return response;
    } catch (error) {
      return { error: error as Error };
    }
  }

  public async anonymiseUser(customerId: string): TRequestResponse<void> {
    try {
      const response = await this.axios.post(
        "/admin/customer/scheduleDeletion",
        { customerId },
      );
      return response.data;
    } catch (error) {
      return { error: error as Error };
    }
  }

  public async refundUser(
    data: IRefundUserRequest,
  ): TRequestResponse<void> {
    try {
      const response = await this.axios.post(
        `/admin/customer/refund?chargeId=${data.chargeId}&amount=${data.refundAmount}&cancelOrder=${data.cancelOrder}&cxUser=${data.cxUser}`,
        {
          refundReason: data.refundReason,
          ticketUrl: data.ticketUrl,
          customerId: data.customerId,
        },
      );
      return response.data;
    } catch (error) {
      return { error: error as Error };
    }
  }

  public async createFreshCustomer(
    details: ICreateFreshCustomer,
  ): Promise<
    | SuccessfulValidationSet<ICreateCustomerResponse>
    | IActionResponseFailure
    > {
    const SUCCESS_STATUS = 200;
    try {
      const response = await this.axios.post("/customer/create", details);
      if (response.status !== SUCCESS_STATUS) {
        const errorMessage = response.data ? `: ${response.data}` : "";
        return {
          success: false,
          message: `Error in creating account ${errorMessage}`,
        };
      }
      return { success: true, value: response.data };
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : "Failed to create customer",
      };
    }
  }

  public async cancelFreshPlan(token: string, request: ICustomerCancelSubscriptionRequest): Promise<boolean> {
    try {
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        withCredentials: true,
      };

      const dataToSend = {
        ...request,
        rechargeSubscriptionId: [ request.rechargeSubscriptionId ],
      };

      const response = await this.axios.delete("/customer/subscriptions", { data: dataToSend, ...config });
      const STATUS_SUCCESS = 200;
      return response.status === STATUS_SUCCESS;
    } catch (error) {
      this.logger.error("Failed to cancel fresh plan", error);
      throw error;
    }
  }

  public async resetPassword(email: string): TRequestResponse<void> {
    try {
      const response = await this.axios.post(
        "/customer/recover",
        { email },
      );
      return response.data;
    } catch (error) {
      return { error: error as Error };
    }
  }

}

export interface AddNibblesTrialRequest {
  productTitle: "NIBBLES_CHICKEN" | "NIBBLES_SALMON";
}

interface ChangeMealPlanRequest {
  mealPlan: Extract<MealPlanType, "Plan28x28"
  | "Plan28x56"
  | "Plan14x14"
  | "PlanTrial14x14">
  reactivate?: boolean | null;
  catIDs?: string[] | null;
  /** Must be in YYYY-MM-DD format */
  deliveryDate?: string | null;
}

export interface UpdateFreshSizeAndCadenceRequest {
  catId: string
  newMealPlan: MealPlanType;
  newSize: PouchSize;
  source: string
  cxUser?: string;
}

export interface ITraySizeResource {
  traySize?: PouchSize;
  calories: number;
  price: number;
}

export interface IMealPlanTypeResource {
  title: string;
  planType: MealPlanType;
  traySize: Record<PouchSize, ITraySizeResource>;
  quantity: number;
  cadence: number;
}

type TMealPlanResponse = Record<MealPlanType, IMealPlanTypeResource>;

export interface IChangeAddonsRequest {
  freshMealPlanId: number;
  addOn: AddOnName;
  quantity: number;
}

export interface IChangeRecipesRequest {
  freshMealPlanId: number;
  newRecipes: Record<FreshRecipeName, number>;
}

export interface IUpdateCustomerDetailsRequest {
  customerId: string;
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
}

export interface IUpdateCatDetailsRequest {
  catId: string;
  weight?: string;
  lifestyle?: string;
  bodyType?: string;
  dateOfBirth?: string;
  name?: string;
  brand?: string;
  breed?: string;
  benefits?: string[];
  benefitImportance?: Record<string, number>;
  feed?: string;
  fussiness?: string;
  gender?: string;
  medicalConditions?: TCatMedicalConditions[];
  medicalConditionsDetails?: string;
  medicalNotes?: string;
  isNeutered?: boolean;
}
export interface IRefundUserRequest {
  chargeId: number,
  refundAmount: number,
  cancelOrder: boolean,
  refundReason: TCreditReason,
  ticketUrl: string,
  customerId: string,
  cxUser: string;
}

interface IFreshCancellationReasonData {
  primaryReason: PrimaryCancellationReason;
  freeText?: string;
}

interface IScoopCancellationReasonData {
  primaryReason: PrimaryScoopCancellationReason;
  freeText?: string;
}

interface NpsCategoryScoring {
  quality?: boolean;
  companyMission?: boolean;
  customerService?: boolean;
  subscriptionService?: boolean;
  packaging?: boolean;
  eatsTheFood?: boolean;
  cost?: boolean;
  healthBenefits?: boolean;
}

interface SurveyResult {
  score?: number;
  reason?: string;
  improvements?: string;
  categoryScoring?: NpsCategoryScoring;
  postKatKinFood?: string;
}

export interface ICustomerCancelSubscriptionRequest {
  rechargeSubscriptionId: number;
  reason: IFreshCancellationReasonData | IScoopCancellationReasonData;
  survey: SurveyResult;
}
